// src/context/ChecklistDefaultsContext.js
import React, { createContext, useContext, useMemo } from "react";
import { useSeasons } from "./SeasonContext";
import { useAlerts } from "./AlertsContext";

const ChecklistDefaultsContext = createContext(null);

// --- Shared helpers (you can move these to a separate file if you like) ---

const severityRank = (type) => {
  if (!type) return 0;
  const t = type.toLowerCase();
  if (t.includes("warning")) return 3;
  if (t.includes("watch")) return 2;
  if (t.includes("burn ban")) return 1;
  return 0;
};

const inferFireWeatherAlertType = (alerts) => {
  let type = null;

  (alerts || [])
    .filter((a) => a.type === "weather")
    .forEach((a) => {
      const props = a.raw?.properties || {};
      const event = (props.event || "").toLowerCase();

      if (event.includes("red flag warning")) {
        type = "Warning";
      } else if (!type && event.includes("fire weather watch")) {
        type = "Watch";
      }
    });

  return type; // "Warning" | "Watch" | null
};

// Build candidates only for supported hazards (Winter + Wildfire for now)
const buildSituationCandidates = (flags, alerts) => {
  const candidates = [];

  // Winter weather
  if (flags?.hasWinterWarning) {
    candidates.push({
      hazard: "Winter Weather",
      alertType: "Warning",
      rank: severityRank("Warning"),
      source: "winterWarning",
    });
  } else if (flags?.hasWinterWatch) {
    candidates.push({
      hazard: "Winter Weather",
      alertType: "Watch",
      rank: severityRank("Watch"),
      source: "winterWatch",
    });
  } else if (flags?.hasWinterStorm) {
    // Generic winter storm flag, treat as at least a Watch
    candidates.push({
      hazard: "Winter Weather",
      alertType: "Watch",
      rank: severityRank("Watch"),
      source: "winterStorm",
    });
  }

  // Wildfire: fire weather (Warning/Watch) + Burn Ban
  const fireWeatherType = flags?.hasFireWeather
    ? inferFireWeatherAlertType(alerts) || "Watch"
    : null;

  if (fireWeatherType) {
    candidates.push({
      hazard: "Wildfire",
      alertType: fireWeatherType,           // "Warning" or "Watch"
      rank: severityRank(fireWeatherType),  // 3 or 2
      source: "fireWeather",
    });
  }

  if (flags?.hasBurnBan) {
    candidates.push({
      hazard: "Wildfire",
      alertType: "Burn Ban",
      rank: severityRank("Burn Ban"),       // 1
      source: "burnBan",
    });
  }

  // Later you can extend this with severe storms, flooding, etc.

  return candidates;
};

export const ChecklistDefaultsProvider = ({ children }) => {
  const { currentSeasons, defaultSeason } = useSeasons();
  const { flags, flagsReady, alerts } = useAlerts();

  const value = useMemo(() => {
    // 1. Seasonal base hazard – driven by defaultSeason first
    let seasonalHazard = "Routine";

    if (defaultSeason?.id === "winterWeather") {
      seasonalHazard = "Winter Weather";
    } else if (defaultSeason?.id === "wildfire") {
      seasonalHazard = "Wildfire";
    } else {
      // Fallback if defaultSeason is not set: original logic
      if (currentSeasons.some((s) => s.id === "wildfire")) {
        seasonalHazard = "Wildfire";
      } else if (currentSeasons.some((s) => s.id === "winterWeather")) {
        seasonalHazard = "Winter Weather";
      }
    }

    let defaultHazard = seasonalHazard;
    let defaultAlertType = "Seasonal";

    if (!flagsReady) {
      return {
        defaultHazard,
        defaultAlertType,
        seasonalHazard,
        candidates: [],
      };
    }

    const hasAnyAlerts = alerts && alerts.length > 0;
    if (!hasAnyAlerts) {
      // No alerts at all → purely seasonal (from defaultSeason)
      return {
        defaultHazard,
        defaultAlertType,
        seasonalHazard,
        candidates: [],
      };
    }

    // 2. Build candidates (Winter + Wildfire)
    const candidates = buildSituationCandidates(flags, alerts);

    if (!candidates.length) {
      // No winter or wildfire flags contributed, stay seasonal
      return {
        defaultHazard,
        defaultAlertType,
        seasonalHazard,
        candidates: [],
      };
    }

    // 3. Pick the single best hazard+alertType pair as the default
    candidates.sort((a, b) => b.rank - a.rank);
    const best = candidates[0];

    defaultHazard = best.hazard;
    defaultAlertType = best.alertType;

    return {
      defaultHazard,
      defaultAlertType,
      seasonalHazard,
      candidates, // expose full list for SituationSuggestions
    };
  }, [
    currentSeasons,
    defaultSeason,
    flagsReady,
    flags?.hasWinterStorm,
    flags?.hasWinterWatch,
    flags?.hasWinterWarning,
    flags?.hasBurnBan,
    flags?.hasFireWeather,
    alerts,
  ]);

  return (
    <ChecklistDefaultsContext.Provider value={value}>
      {children}
    </ChecklistDefaultsContext.Provider>
  );
};

export const useChecklistDefaults = () => {
  const ctx = useContext(ChecklistDefaultsContext);
  if (!ctx) {
    throw new Error(
      "useChecklistDefaults must be used within ChecklistDefaultsProvider"
    );
  }
  return ctx;
};
